import {
    Box,
    InputLabel,
    Link,
    Paper,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { Control, Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
    useEditReleaseSettingsMutation,
    useGetReleaseSettingsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { ReleaseNotesAudience } from '../../../../feature/types/release-notes';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import FormDateFilter from '../../../shared/FormDateFilter';
import FormMultiAutocomplete from '../../../shared/FormMultiAutocomplete';
import FormTextField from '../../../shared/FormTextField';
import toast from '../../../shared/Toast';

export const DEFAULT_RELEASE_NOTES_TITLE = 'Fresh out of the oven!';
export const DEFAULT_RELEASE_NOTES_MESSAGE =
    "We've shipped a new 7DAY update with smoother booking tools and a few handy upgrades.";
export const DEFAULT_RELEASE_NOTES_URL =
    'https://help.7-day.com/hc/en-us/categories/14156313135759-Release-Notes';
const AUDIENCE_OPTIONS: { value: ReleaseNotesAudience; label: string }[] = [
    { value: 'owner', label: 'Owners' },
    { value: 'guest', label: 'Guests' },
    { value: 'admin', label: '7DAY Admins' },
    { value: 'contractor', label: 'Contractors' },
];

const Schema = z.object({
    enabled: z.boolean(),
    releasedAt: z.string().min(1, 'Release date is required'),
    releaseNotesUrl: z.string().url('Enter a valid URL'),
    title: z.string().min(1, 'Title is required'),
    message: z.string().min(1, 'Message is required'),
    appliesTo: z
        .array(z.enum(['owner', 'guest', 'admin', 'contractor']))
        .min(1, 'Select at least one audience'),
});

type ReleaseNotesSettingsFormValues = z.infer<typeof Schema>;

type ResettableTextFieldProps = {
    name: 'releaseNotesUrl' | 'title' | 'message';
    label: string;
    value: string;
    defaultValue: string;
    control: Control<ReleaseNotesSettingsFormValues>;
    onReset: () => void;
    multiline?: boolean;
    minRows?: number;
    placeholder?: string;
};

const ResettableTextField = ({
    name,
    label,
    value,
    defaultValue,
    control,
    onReset,
    multiline,
    minRows,
    placeholder,
}: ResettableTextFieldProps) => (
    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={1}
        >
            <InputLabel sx={{ m: 0, position: 'static', transform: 'none' }}>
                {label}
            </InputLabel>
            {value !== defaultValue ? (
                <Link
                    component='button'
                    type='button'
                    variant='body2'
                    underline='hover'
                    onClick={onReset}
                    sx={{ fontSize: 13, flexShrink: 0 }}
                >
                    Set to default
                </Link>
            ) : null}
        </Stack>
        <FormTextField
            name={name}
            control={control}
            multiline={multiline}
            minRows={minRows}
            placeholder={placeholder}
        />
    </Stack>
);

const ReleaseNotesSettingsForm = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const { data: settings, isLoading } = useGetReleaseSettingsQuery(
        undefined,
        {
            skip: loggedInUser.role !== 'admin',
        },
    );
    const [editReleaseSettings] = useEditReleaseSettingsMutation();

    useEffect(() => {
        if (loggedInUser.role !== 'admin') {
            navigate('/permissionDenied');
        }
    }, [loggedInUser.role, navigate]);

    const {
        control,
        reset,
        setValue,
        watch,
        handleSubmit,
        formState: { isSubmitting, isDirty },
    } = useForm<ReleaseNotesSettingsFormValues>({
        resolver: zodResolver(Schema),
        defaultValues: {
            enabled: true,
            releasedAt: '',
            releaseNotesUrl: DEFAULT_RELEASE_NOTES_URL,
            title: DEFAULT_RELEASE_NOTES_TITLE,
            message: DEFAULT_RELEASE_NOTES_MESSAGE,
            appliesTo: [],
        },
    });

    const releaseNotesUrl = watch('releaseNotesUrl');
    const title = watch('title');
    const message = watch('message');

    useEffect(() => {
        if (settings) {
            reset({
                enabled: settings.enabled,
                releasedAt: settings.releasedAt,
                releaseNotesUrl: settings.releaseNotesUrl,
                title: settings.title,
                message: settings.message,
                appliesTo: settings.appliesTo,
            });
        }
    }, [settings, reset]);

    const onSubmit = async (data: ReleaseNotesSettingsFormValues) => {
        try {
            await editReleaseSettings(data).unwrap();
            reset(data);
            toast.success('Release notes settings saved successfully');
        } catch {
            toast.error(
                'Failed to update release notes settings. Please try again.',
            );
        }
    };

    if (isLoading) {
        return <LoadingComponent />;
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
                spacing={{ xs: 2.5, sm: 4, md: 5 }}
                sx={{ width: '100%', minWidth: 0 }}
            >
                <Typography
                    variant='h1'
                    sx={{
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        fontSize: { xs: '1.35rem', sm: '1.75rem', md: '2rem' },
                        lineHeight: 1.2,
                    }}
                >
                    Release Notes Prompt
                </Typography>

                <Typography
                    color='text.primary'
                    sx={{ fontSize: { xs: 14, sm: 16 }, lineHeight: 1.5 }}
                >
                    Configure the in-app release announcement shown to users
                    after a new release. Changing the release date will show the
                    prompt again from that date for 21 days to users who have
                    not acknowledged it.
                </Typography>

                <Paper
                    sx={{
                        p: { xs: 2, sm: 3 },
                    }}
                >
                    <Stack spacing={{ xs: 2, sm: 2.5 }}>
                        <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            gap={1}
                            sx={{ width: '100%' }}
                        >
                            <InputLabel sx={{ m: 0 }}>
                                Enable release prompt
                            </InputLabel>
                            <Controller
                                name='enabled'
                                control={control}
                                render={({ field }) => (
                                    <Switch
                                        checked={field.value}
                                        onChange={(event) =>
                                            field.onChange(event.target.checked)
                                        }
                                    />
                                )}
                            />
                        </Stack>

                        <FormDateFilter
                            name='releasedAt'
                            control={control}
                            label='Release date'
                        />

                        <ResettableTextField
                            name='releaseNotesUrl'
                            label='Release notes URL'
                            value={releaseNotesUrl}
                            defaultValue={DEFAULT_RELEASE_NOTES_URL}
                            control={control}
                            placeholder='https://help.7-day.com/...'
                            onReset={() =>
                                setValue(
                                    'releaseNotesUrl',
                                    DEFAULT_RELEASE_NOTES_URL,
                                    { shouldDirty: true },
                                )
                            }
                        />

                        <ResettableTextField
                            name='title'
                            label='Title'
                            value={title}
                            defaultValue={DEFAULT_RELEASE_NOTES_TITLE}
                            control={control}
                            onReset={() =>
                                setValue('title', DEFAULT_RELEASE_NOTES_TITLE, {
                                    shouldDirty: true,
                                })
                            }
                        />

                        <ResettableTextField
                            name='message'
                            label='Message'
                            value={message}
                            defaultValue={DEFAULT_RELEASE_NOTES_MESSAGE}
                            control={control}
                            multiline
                            minRows={3}
                            onReset={() =>
                                setValue(
                                    'message',
                                    DEFAULT_RELEASE_NOTES_MESSAGE,
                                    {
                                        shouldDirty: true,
                                    },
                                )
                            }
                        />

                        <FormMultiAutocomplete
                            name='appliesTo'
                            control={control}
                            label='Audience'
                            placeholder='Select roles'
                            options={AUDIENCE_OPTIONS}
                        />
                    </Stack>
                </Paper>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                    }}
                >
                    <CustomButton
                        type='submit'
                        variant='contained'
                        color='primary'
                        disabled={!isDirty || isSubmitting}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Save settings
                    </CustomButton>
                </Box>
            </Stack>
        </form>
    );
};

export default ReleaseNotesSettingsForm;
